import { cardQueueUpdatedAt, isClosedQueueCard } from "@/lib/financialWork/feedClient";
import {
  isReceivableBatchCard,
  isSlipOwnerAskCard,
  workStatusLabel,
} from "@/lib/financialWork/nav";
import type { QueueSortColumn } from "@/lib/financialWork/queueRowSort";
import type { LiffFinancialWorkCard } from "@/lib/financialWork/types";
import { stripStatusEmoji, type TableFilters } from "@aera/data-table";

/** Matches แท็บข้อมูล workers: รายรับ · รายจ่าย · ยอดค้าง. */
export type WorkQueueCardKind = "รายรับ" | "รายจ่าย" | "ยอดค้าง";

const BANGKOK_TZ = "Asia/Bangkok";

function isPartialReceivableCard(card: LiffFinancialWorkCard): boolean {
  const paid = card.outstanding?.items?.[0]?.paidAmountMinor ?? 0;
  const remaining =
    card.outstanding?.remainingAmountMinor ??
    (card.amountSummary?.basis === "remaining"
      ? card.amountSummary.primaryAmountMinor
      : null);
  return paid > 0 && remaining != null && remaining > 0;
}

function isUnpaidInvoice(card: LiffFinancialWorkCard, statusTitle: string): boolean {
  return (
    statusTitle === "ต้องระวัง" ||
    /invoice|ยังจ่ายไม่ได้|ยังไม่จ่าย/i.test(card.title ?? "")
  );
}

/** Same status label as the queue table column. */
export function workQueueDesktopStatusTitle(card: LiffFinancialWorkCard): string {
  const next = card.nextStep?.label ?? "";
  let title = workStatusLabel(card);
  if (
    isSlipOwnerAskCard(card) ||
    title === "ต้องเลือกยอด" ||
    title === "รอการตัดสินใจ"
  ) {
    return "รอการตัดสินใจ";
  }
  if (isPartialReceivableCard(card)) {
    return "จ่ายบางส่วน รอสลิป";
  }
  if (card.surfaceState === "risk" || next.includes("ตรวจบัญชี")) {
    title = "ต้องระวัง";
  } else if (
    title === "รอสลิป" ||
    next.includes("จับคู่") ||
    next.includes("เลือกยอด")
  ) {
    title = "รอจับคู่";
  } else if (card.surfaceState === "needs_review" || title === "ต้องตรวจ") {
    title = "ต้องตรวจ";
  }
  if (isUnpaidInvoice(card, title)) {
    return "ยังไม่จ่าย";
  }
  if (card.requirementStatus?.complete || isClosedQueueCard(card)) {
    return "ปิดยอดแล้ว";
  }
  return title;
}

export function workQueueCardDateKey(card: LiffFinancialWorkCard): string | null {
  const raw = cardQueueUpdatedAt(card);
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: BANGKOK_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(ms));
}

function cardWorkerId(card: LiffFinancialWorkCard): string | null {
  return (
    card.availableActions.find((action) => action.dataNav?.workerId)?.dataNav
      ?.workerId ?? null
  );
}

function isOutstandingKindCard(card: LiffFinancialWorkCard): boolean {
  if (card.objectRef.kind === "expense") return false;
  if (card.objectRef.kind === "receivable" || isReceivableBatchCard(card)) {
    return true;
  }
  const workerId = cardWorkerId(card);
  if (workerId === "bill-lin-expense") return false;
  if (workerId === "bill-lin-receivable") return true;
  const hay = `${card.title} ${card.subtitle ?? ""}`;
  // Closed match labels like จับคู่กับยอดค้างที่เลือก are not ยอดค้าง rows.
  if (/จับคู่กับยอดค้าง|จับคู่เอง|จับคู่ยอดอัตโนมัติ|ปิดยอดอัตโนมัติ/.test(hay)) {
    return false;
  }
  if (/ยอดค้าง|ชุดยอดค้าง/.test(hay)) return true;
  if (/\sค้าง\s+[\d,.]+\s*บาท/u.test(card.title ?? "")) return true;
  return false;
}

function isExpenseKindCard(card: LiffFinancialWorkCard): boolean {
  const workerId = cardWorkerId(card);
  if (workerId === "bill-lin-expense" || card.objectRef.kind === "expense") {
    return true;
  }
  const hay = `${card.title} ${card.subtitle ?? ""} ${card.objectRef.kind}`;
  return /ใบเสร็จ|รายจ่าย|เงินออก|ตลาดสด|เงินสดหน้าร้าน/.test(hay);
}

/** Classify a queue card into รายรับ / รายจ่าย / ยอดค้าง for FilterBar category. */
export function workQueueCardKind(card: LiffFinancialWorkCard): WorkQueueCardKind {
  const kind = String(card.objectRef?.kind || "").toLowerCase();
  if (kind === "expense") return "รายจ่าย";
  if (kind === "receivable" || isReceivableBatchCard(card)) return "ยอดค้าง";
  if (isOutstandingKindCard(card)) return "ยอดค้าง";
  if (isExpenseKindCard(card)) return "รายจ่าย";
  return "รายรับ";
}

export function workQueueHasClientFilters(filters: TableFilters): boolean {
  return Boolean(
    filters.status?.trim() ||
      filters.from?.trim() ||
      filters.to?.trim() ||
      filters.category?.trim(),
  );
}

function queueFilterStatusLabel(value: string | null | undefined): string | null {
  const label = stripStatusEmoji((value ?? "").trim()).trim();
  return label || null;
}

export function workQueueCardMatchesFilters(
  card: LiffFinancialWorkCard,
  filters: TableFilters,
): boolean {
  const status = queueFilterStatusLabel(filters.status);
  if (status && workQueueDesktopStatusTitle(card) !== status) {
    return false;
  }

  const category = filters.category?.trim() || null;
  if (category && workQueueCardKind(card) !== category) {
    return false;
  }

  const from = filters.from?.trim() || null;
  const to = filters.to?.trim() || null;
  if (from || to) {
    const key = workQueueCardDateKey(card);
    if (!key) return false;
    if (from && key < from) return false;
    if (to && key > to) return false;
  }

  return true;
}

export function workQueueFilterSortColumn(
  sort: string | null | undefined,
): QueueSortColumn | null {
  switch (sort) {
    case "record_date":
      return "date";
    case "money_in":
      return "money_in";
    case "money_out":
      return "money_out";
    case "related":
      return "related";
    default:
      return null;
  }
}

export function createDefaultWorkQueueFilters(): TableFilters {
  return {
    status: null,
    from: null,
    to: null,
    q: null,
    sort: "record_date",
    dir: "desc",
    category: null,
    nameToken: null,
  };
}
